/*
 * Request Price - Cart Guard
 *
 * Products tagged 'Request Price' are quote-only: the theme hides their price
 * and removes every add-to-cart path. This is the backstop for the cases the
 * theme cannot cover:
 *   - a cart built BEFORE the product was tagged (stale / abandoned carts)
 *   - third-party widgets with their own add-to-cart (e.g. Searchanise results)
 *   - a hand-crafted POST to /cart/add
 *
 * Tags are read per-product from /products/<handle>.js (which exposes `tags`),
 * so there is no list to maintain: tag the product in Shopify and it is covered.
 *
 * NOTE: this is a client-side guard. It reliably cleans up real customer carts,
 * but it is not a server-side rule. Shopify Functions (Cart and Checkout
 * Validation) is the only way to make this unbypassable at checkout.
 */
(function () {
  var TAG = 'request price';
  var CHECK_DEBOUNCE_MS = 150;

  // Capture fetch before any other script (ours or an app's) wraps it, so our
  // own cart calls cannot re-trigger this guard or another patched handler.
  var nativeFetch = window.fetch ? window.fetch.bind(window) : null;
  if (!nativeFetch) return;

  var tagCache = {};
  var running = false;
  var pending = null;

  document.addEventListener('DOMContentLoaded', function () {
    patchFetch();
    patchXHR();
    scheduleCheck();

    ['cart:refresh', 'cart:updated', 'drawer:open'].forEach(function (evt) {
      document.addEventListener(evt, scheduleCheck);
    });
  });

  function root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function json(url, options) {
    return nativeFetch(url, options).then(function (res) {
      if (!res.ok) throw new Error(url + ' -> ' + res.status);
      return res.json();
    });
  }

  // ---- tag lookup (cached per handle: one request per product per page) ----
  function productTags(handle) {
    if (!handle) return Promise.resolve([]);
    if (tagCache[handle]) return tagCache[handle];

    tagCache[handle] = json(root() + 'products/' + handle + '.js')
      .then(function (product) {
        var tags = product && Array.isArray(product.tags) ? product.tags : [];
        return tags.map(function (t) {
          return String(t).trim().toLowerCase();
        });
      })
      .catch(function () {
        // Fail open: a lookup error must never wipe a legitimate cart.
        return [];
      });

    return tagCache[handle];
  }

  function isQuoteOnly(item) {
    return productTags(item.handle).then(function (tags) {
      return tags.indexOf(TAG) !== -1 ? item : null;
    });
  }

  // ---- main guard ----
  function scheduleCheck() {
    clearTimeout(pending);
    pending = setTimeout(guardCart, CHECK_DEBOUNCE_MS);
  }

  function guardCart() {
    if (running) return;
    running = true;

    json(root() + 'cart.js')
      .then(function (cart) {
        if (!cart || !cart.items || !cart.items.length) return [];
        return Promise.all(cart.items.map(isQuoteOnly));
      })
      .then(function (results) {
        var offenders = results.filter(Boolean);
        return offenders.length ? removeLines(offenders) : [];
      })
      .then(function (removed) {
        running = false;
        if (!removed.length) return;
        notify(removed);
        refreshCartUI();
      })
      .catch(function (error) {
        running = false;
        console.warn('[request-price] cart guard skipped:', error);
      });
  }

  // Remove one line at a time: line keys shift if you batch them.
  function removeLines(items) {
    var removed = [];

    return items
      .reduce(function (chain, item) {
        return chain.then(function () {
          return nativeFetch(root() + 'cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({ id: item.key, quantity: 0 })
          }).then(function (res) {
            if (res.ok) removed.push(item);
          });
        });
      }, Promise.resolve())
      .then(function () {
        return removed;
      });
  }

  // ---- catch adds as they happen, not just on the next page load ----
  function patchFetch() {
    if (window.__rpFetchPatched) return;
    window.__rpFetchPatched = true;

    var previousFetch = window.fetch;

    window.fetch = function (resource, options) {
      var result = previousFetch.apply(this, arguments);
      var url =
        typeof resource === 'string' ? resource : (resource && resource.url) || '';

      // Only observe the response status. Never read the body, or the theme's
      // own handler would receive an already-consumed stream.
      if (url.indexOf('/cart/add') !== -1) {
        result
          .then(function (res) {
            if (res && res.ok) scheduleCheck();
          })
          .catch(function () {});
      }

      return result;
    };
  }

  function patchXHR() {
    if (window.__rpXhrPatched || !window.XMLHttpRequest) return;
    window.__rpXhrPatched = true;

    var open = XMLHttpRequest.prototype.open;
    var send = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this.__rpUrl = typeof url === 'string' ? url : '';
      return open.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      if (this.__rpUrl && this.__rpUrl.indexOf('/cart/add') !== -1) {
        this.addEventListener('load', scheduleCheck);
      }
      return send.apply(this, arguments);
    };
  }

  // ---- UI ----
  function refreshCartUI() {
    if (/\/cart\/?$/.test(window.location.pathname)) {
      window.location.reload();
      return;
    }

    if (typeof window.publish === 'function') {
      try {
        window.publish('cart-update', { source: 'request-price-guard' });
      } catch (e) {
        /* no-op */
      }
    }

    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  function notify(items) {
    var titles = items.map(function (item) {
      return item.product_title || item.title || 'This item';
    });

    var message =
      titles.length === 1
        ? '"' + titles[0] + '" is available by quote only, so it has been removed from your cart.'
        : titles.length + ' items in your cart are available by quote only and have been removed.';

    message += ' Please use the Request Price button on the product page to get pricing.';

    var existing = document.getElementById('rp-cart-notice');
    if (existing) existing.remove();

    var banner = document.createElement('div');
    banner.id = 'rp-cart-notice';
    banner.setAttribute('role', 'status');
    banner.style.cssText = [
      'position:fixed',
      'z-index:100000',
      'top:1.5rem',
      'left:50%',
      'transform:translateX(-50%)',
      'max-width:min(46rem, calc(100vw - 3rem))',
      'box-sizing:border-box',
      'padding:1.2rem 3.4rem 1.2rem 1.4rem',
      'background:#fff',
      'color:#1a1a1a',
      'border-left:4px solid #17a1ad',
      'border-radius:4px',
      'box-shadow:0 4px 18px rgba(0,0,0,.18)',
      'font-size:1.4rem',
      'line-height:1.45'
    ].join(';');
    banner.textContent = message;

    var close = document.createElement('button');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close');
    close.textContent = '✕';
    close.style.cssText = [
      'position:absolute',
      'top:.5rem',
      'right:.6rem',
      'border:0',
      'background:none',
      'cursor:pointer',
      'font-size:1.6rem',
      'line-height:1',
      'padding:.6rem',
      'color:inherit'
    ].join(';');
    close.addEventListener('click', function () {
      banner.remove();
    });

    banner.appendChild(close);
    document.body.appendChild(banner);

    setTimeout(function () {
      if (banner.isConnected) banner.remove();
    }, 12000);
  }
})();